import { useState, type Dispatch, type SetStateAction } from "react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Briefcase } from "lucide-react";
import { useCountries } from "@/hooks";
import type { BusinessKycFormData } from "@/interfaces/kyc";
import type { Country } from "@/interfaces/country";

interface Props {
  isLevelInputLocked: boolean;
  businessInfo: BusinessKycFormData;
  setBusinessInfo: Dispatch<SetStateAction<BusinessKycFormData>>;
  countries: NoInfer<Country[]> | undefined;
  isLoading: false;
}

const BusinessInfo = ({
  isLevelInputLocked,
  businessInfo,
  setBusinessInfo,
  countries,
  isLoading,
}: Props) => {
  return (
    <div className="space-y-4">
      <div>
        <Label className="mb-2">
          Business Name <span className="text-red-500">*</span>
        </Label>
        <Input
          value={businessInfo.businessName}
          onChange={(e) =>
            setBusinessInfo({
              ...businessInfo,
              businessName: e.target.value,
            })
          }
          className="!h-12"
          placeholder="Enter business name"
          disabled={isLevelInputLocked}
          readOnly={isLevelInputLocked}
        />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="mb-2">
            Registration Number <span className="text-red-500">*</span>
          </Label>
          <Input
            value={businessInfo.registrationNumber}
            onChange={(e) =>
              setBusinessInfo({
                ...businessInfo,
                registrationNumber: e.target.value,
              })
            }
            className="!h-12"
            placeholder="Business registration number"
            disabled={isLevelInputLocked}
            readOnly={isLevelInputLocked}
          />
        </div>
        <div>
          <Label className="mb-2">
            Business Type <span className="text-red-500">*</span>
          </Label>
          <select
            className="w-full h-11 px-3 rounded-md border border-input bg-background"
            value={businessInfo.businessType}
            onChange={(e) =>
              setBusinessInfo({
                ...businessInfo,
                businessType: e.target.value,
              })
            }
            disabled={isLevelInputLocked}
          >
            <option value="">Select type</option>
            <option value="sole_proprietorship">Sole Proprietorship</option>
            <option value="partnership">Partnership</option>
            <option value="llc">Limited Liability Company</option>
            <option value="corporation">Corporation</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="mb-2">Tax ID</Label>
          <Input
            value={businessInfo.taxId}
            onChange={(e) =>
              setBusinessInfo({
                ...businessInfo,
                taxId: e.target.value,
              })
            }
            className="!h-12"
            placeholder="Tax identification number"
            disabled={isLevelInputLocked}
            readOnly={isLevelInputLocked}
          />
        </div>
        <div>
          <Label className="mb-2">Website</Label>
          <Input
            value={businessInfo.website}
            onChange={(e) =>
              setBusinessInfo({
                ...businessInfo,
                website: e.target.value,
              })
            }
            className="!h-12"
            placeholder="https://example.com"
            disabled={isLevelInputLocked}
            readOnly={isLevelInputLocked}
          />
        </div>
      </div>
      <div>
        <Label className="mb-2">
          Business Address <span className="text-red-500">*</span>
        </Label>
        <Input
          value={businessInfo.businessAddress}
          onChange={(e) =>
            setBusinessInfo({
              ...businessInfo,
              businessAddress: e.target.value,
            })
          }
          className="!h-12"
          placeholder="Business street address"
          disabled={isLevelInputLocked}
          readOnly={isLevelInputLocked}
        />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label className="mb-2">City</Label>
          <Input
            value={businessInfo.businessCity}
            onChange={(e) =>
              setBusinessInfo({
                ...businessInfo,
                businessCity: e.target.value,
              })
            }
            className="!h-12"
            placeholder="City"
            disabled={isLevelInputLocked}
            readOnly={isLevelInputLocked}
          />
        </div>
        <div>
          <Label className="mb-2">State/Province</Label>
          <Input
            value={businessInfo.businessState}
            onChange={(e) =>
              setBusinessInfo({
                ...businessInfo,
                businessState: e.target.value,
              })
            }
            className="!h-12"
            placeholder="State"
            disabled={isLevelInputLocked}
            readOnly={isLevelInputLocked}
          />
        </div>
        <div>
          <Label className="mb-2">Country</Label>
          <select
            className="w-full h-11 px-3 rounded-md border border-input bg-background"
            value={businessInfo.businessCountry}
            onChange={(e) =>
              setBusinessInfo({
                ...businessInfo,
                businessCountry: e.target.value,
              })
            }
            disabled={isLevelInputLocked}
          >
            <option value="">Select country</option>
            {isLoading
              ? "Loading countries"
              : countries?.map((country) => (
                  <option key={country.id} value={country.name}>
                    {country.name}
                  </option>
                ))}
          </select>
        </div>
      </div>
      <div className="border-t border-border pt-4 mt-4">
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <Briefcase className="w-4 h-4" /> Director Information
        </h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-2">
              Director's Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              value={businessInfo.directorName}
              onChange={(e) =>
                setBusinessInfo({
                  ...businessInfo,
                  directorName: e.target.value,
                })
              }
              className="!h-12"
              placeholder="Director's full name"
              disabled={isLevelInputLocked}
              readOnly={isLevelInputLocked}
            />
          </div>
          <div>
            <Label className="mb-2">
              Director's ID Number <span className="text-red-500">*</span>
            </Label>
            <Input
              value={businessInfo.directorIdNumber}
              onChange={(e) =>
                setBusinessInfo({
                  ...businessInfo,
                  directorIdNumber: e.target.value,
                })
              }
              className="!h-12"
              placeholder="ID number"
              disabled={isLevelInputLocked}
              readOnly={isLevelInputLocked}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessInfo;
